'use client';

import { Collapsible as CollapsiblePrimitive } from 'radix-ui';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { playSfx } from '@/hooks/use-sound';

import type { ChevronsDownUpIconHandle } from '../animated-icons/chevrons-down-up-icon';
import { ChevronsDownUpIcon } from '../animated-icons/chevrons-down-up-icon';

function Collapsible(
  props: React.ComponentProps<typeof CollapsiblePrimitive.Root>
) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

function CollapsibleTrigger(
  props: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>
) {
  return (
    <CollapsiblePrimitive.CollapsibleTrigger
      data-slot="collapsible-trigger"
      // Plays its own open/close sound; suppress the global click sound.
      data-sfx-managed=""
      {...props}
    />
  );
}

function CollapsibleContent(
  props: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent>
) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      data-slot="collapsible-content"
      {...props}
    />
  );
}

type CollapsibleContextType = {
  open: boolean;
};

const CollapsibleContext = createContext<CollapsibleContextType | null>(null);

const useCollapsible = () => {
  const context = useContext(CollapsibleContext);

  if (!context) {
    throw new Error(
      'Collapsible components must be used within a CollapsibleWithContext'
    );
  }

  return context;
};

function CollapsibleWithContext({
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof Collapsible>) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    playSfx(nextOpen ? 'open' : 'close');
    onOpenChange?.(nextOpen);
  };

  return (
    <CollapsibleContext.Provider value={{ open }}>
      <Collapsible open={open} onOpenChange={handleOpenChange} {...props} />
    </CollapsibleContext.Provider>
  );
}

function CollapsibleChevronsIcon() {
  const { open } = useCollapsible();

  const ref = useRef<ChevronsDownUpIconHandle>(null);

  useEffect(() => {
    const controls = ref.current;
    if (!controls) return;

    if (open) {
      controls.startAnimation();
    } else {
      controls.stopAnimation();
    }
  }, [open]);

  return <ChevronsDownUpIcon ref={ref} />;
}

export {
  Collapsible,
  CollapsibleChevronsIcon,
  CollapsibleContent,
  CollapsibleTrigger,
  CollapsibleWithContext,
};
